'use client';

import { c as _c } from "react/compiler-runtime";
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { Pagination, TableNoFilterResults } from '@morpho-org/ui-v6/client';
import { useTable } from '@tanstack/react-table';
import { useQueryStates } from 'nuqs';
import { useTransition } from 'react';
import type { VaultListOutput } from '@/lib/procedures/vaultList.contract';
import { MobileVaultCard } from '@/components/common/MobileEarnCards';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import useTableSettings from '@/helpers/cookieStorage/userSettings/tableSettings/client';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { getVaultUrl } from '@/helpers/routing';
import { useIsMobile } from '@/hooks/useBreakpoints';
import type { CuratorAssetOptions } from './config/curatorAssetOptions';
import { CuratorVaultsTableFilters } from './components';
import { COLUMN_LABELS, columnsCuratorVaults, curatorVaultsTableFeatures } from './config/CuratorVaultsTable.columns';
import { CURATOR_VAULTS_DEFAULT_TABLE_SETTINGS, CURATOR_VAULTS_STORAGE_KEY, type CuratorVaultsFilters, curatorVaultsUrlFilterParser, PAGE_SIZE } from './config/CuratorVaultsTable.config';
import { useApplyCuratorVaultsFilters } from './hooks/useCuratorVaultsTableFilters';
import { useCuratorVaultsTableRows } from './hooks/useCuratorVaultsTableRows';
import CuratorVaultsTableError from './states/CuratorVaultsTableError';
import CuratorVaultsTableSkeleton from './states/CuratorVaultsTableSkeleton';
export interface CuratorVaultsTableProps {
  vaultsData: VaultListOutput;
  assetOptions: CuratorAssetOptions;
}
const CuratorVaultsTable = t0 => {
  const $ = _c(56);
  const {
    vaultsData,
    assetOptions
  } = t0;
  const [isPending, startTransition] = useTransition();
  const [filters, setFilters] = useQueryStates(curatorVaultsUrlFilterParser);
  const isMobile = useIsMobile();
  let t1;
  if ($[0] !== setFilters) {
    t1 = newFilters => {
      startTransition(() => {
        setFilters(newFilters);
      });
    };
    $[0] = setFilters;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onFiltersChange = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      pageKey: PageKey.Curator,
      tableSettingsObjectKey: CURATOR_VAULTS_STORAGE_KEY,
      defaultTableSettings: CURATOR_VAULTS_DEFAULT_TABLE_SETTINGS
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    sorting,
    setSorting,
    columnVisibility,
    setColumnVisibility
  } = useTableSettings(t2);
  let t3;
  if ($[3] !== vaultsData.items) {
    t3 = {
      vaults: vaultsData.items
    };
    $[3] = vaultsData.items;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const vaultsTableData = useCuratorVaultsTableRows(t3);
  let t4;
  if ($[5] !== filters || $[6] !== vaultsTableData) {
    t4 = {
      items: vaultsTableData,
      filters
    };
    $[5] = filters;
    $[6] = vaultsTableData;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    filteredItems,
    itemsCount: filteredItemsCount
  } = useApplyCuratorVaultsFilters(t4);
  let t5;
  if ($[8] !== columnVisibility || $[9] !== sorting) {
    t5 = {
      sorting,
      columnVisibility
    };
    $[8] = columnVisibility;
    $[9] = sorting;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      pagination: {
        pageSize: PAGE_SIZE,
        pageIndex: 0
      }
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== filteredItems || $[13] !== setColumnVisibility || $[14] !== setSorting || $[15] !== t5) {
    t7 = {
      features: curatorVaultsTableFeatures,
      data: filteredItems,
      onSortingChange: setSorting,
      state: t5,
      initialState: t6,
      columns: columnsCuratorVaults,
      enableSortingRemoval: false,
      sortDescFirst: true,
      onColumnVisibilityChange: setColumnVisibility
    };
    $[12] = filteredItems;
    $[13] = setColumnVisibility;
    $[14] = setSorting;
    $[15] = t5;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const vaultsTable = useTable(t7);
  const pagination = vaultsTable.state.pagination;
  let t8;
  if ($[17] !== vaultsTable) {
    t8 = vaultsTable.getRowModel();
    $[17] = vaultsTable;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const hasRows = t8.rows.length > 0;
  const showEmptyState = !hasRows && !isPending;
  const hasFilters = !!filters.search || filters.assets.length > 0 || filters.chains.length > 0 || !!filters.v2;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <CuratorVaultsTableError />;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== assetOptions || $[21] !== filters || $[22] !== onFiltersChange) {
    t10 = <CuratorVaultsTableFilters assetOptions={assetOptions} filters={filters} onFiltersChange={onFiltersChange} withGlobalSearch={true} disabled={false} withNetworkFilter={true} />;
    $[20] = assetOptions;
    $[21] = filters;
    $[22] = onFiltersChange;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== vaultsData.partial) {
    t11 = vaultsData.partial && <p data-testid="curator-vaults-partial-notice" className="text-secondary-foreground text-sm">Some vaults could not be loaded, so this list may be incomplete.</p>;
    $[24] = vaultsData.partial;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== filters || $[27] !== hasFilters || $[28] !== hasRows || $[29] !== isMobile || $[30] !== isPending || $[31] !== onFiltersChange || $[32] !== showEmptyState || $[33] !== vaultsData.items || $[34] !== vaultsTable) {
    t12 = <div className="grid h-full w-full overflow-auto">{isPending ? <CuratorVaultsTableSkeleton withFilters={false} withPagination={false} withHeader={!isMobile} rows={PAGE_SIZE} /> : <>{isMobile ? <div className="gap-sm flex min-w-0 flex-col">{vaultsTable.getRowModel().rows.map(_temp)}</div> : <>{hasRows && <Table.Table><Table.Thead>{vaultsTable.getHeaderGroups().map(_temp2)}</Table.Thead><Table.Tbody>{vaultsTable.getRowModel().rows.map(_temp3)}</Table.Tbody></Table.Table>}</>}{showEmptyState && <TableNoFilterResults type="vaults" count={vaultsData.items.length} hasFilters={hasFilters} clearFilters={() => onFiltersChange({
          ...filters,
          search: "",
          assets: [],
          chains: [],
          v2: false
        })} />}</>}</div>;
    $[26] = filters;
    $[27] = hasFilters;
    $[28] = hasRows;
    $[29] = isMobile;
    $[30] = isPending;
    $[31] = onFiltersChange;
    $[32] = showEmptyState;
    $[33] = vaultsData.items;
    $[34] = vaultsTable;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const t13 = vaultsTable.previousPage;
  const t14 = vaultsTable.nextPage;
  const t15 = vaultsTable.setPageIndex;
  let t16;
  if ($[36] !== vaultsTable) {
    t16 = vaultsTable.getCanPreviousPage();
    $[36] = vaultsTable;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== vaultsTable) {
    t17 = vaultsTable.getCanNextPage();
    $[38] = vaultsTable;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== filteredItemsCount || $[41] !== isPending || $[42] !== pagination || $[43] !== t16 || $[44] !== t17 || $[45] !== vaultsTable.nextPage || $[46] !== vaultsTable.previousPage || $[47] !== vaultsTable.setPageIndex) {
    t18 = <Pagination loading={isPending} error={null} pagination={pagination} totalItemsCount={filteredItemsCount} previousPage={t13} nextPage={t14} setPage={t15} canPreviousPage={t16} canNextPage={t17} />;
    $[40] = filteredItemsCount;
    $[41] = isPending;
    $[42] = pagination;
    $[43] = t16;
    $[44] = t17;
    $[45] = vaultsTable.nextPage;
    $[46] = vaultsTable.previousPage;
    $[47] = vaultsTable.setPageIndex;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t12 || $[50] !== t18) {
    t19 = <div className="gap-2xs max-md:gap-md flex h-full w-full flex-col">{t12}{t18}</div>;
    $[49] = t12;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t10 || $[53] !== t11 || $[54] !== t19) {
    t20 = <SectionErrorBoundary title="Error loading Curator Table data" description="Something went wrong while loading the Curator Table data" fallback={t9}><div className="gap-md flex flex-col">{t10}{t11}{t19}</div></SectionErrorBoundary>;
    $[52] = t10;
    $[53] = t11;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  return t20;
};
export default CuratorVaultsTable;
function _temp(row) {
  const {
    vault,
    deposits,
    apy,
    exposure
  } = row.original;
  const vaultUrl = getVaultUrl({
    identifier: InterfaceChainUtils.getIdentifierById(vault.chainId),
    vaultAddress: vault.address,
    vaultName: vault.name
  });
  return <MobileVaultCard key={row.id} href={vaultUrl} vault={{
    address: vault.address,
    name: vault.name,
    asset: vault.asset,
    chainId: vault.chainId,
    warnings: vault.warnings,
    isVaultV2: vault.version === "2.0"
  }} apy={{
    native: apy.apy,
    net: apy.netApy,
    rewards: apy.rewards,
    performanceFee: apy.performanceFee,
    maxApy: apy.maxApy,
    isRateCapped: apy.isRateCapped,
    includesDonations: apy.includesDonations,
    apyBreakdown: apy.apyBreakdown,
    points: []
  }} totalDeposits={{
    amount: deposits.amount,
    usdValue: deposits.valueUsd
  }} curators={[]} collateralExposures={exposure.collateralExposures} />;
}
function _temp2(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} columnLabels={COLUMN_LABELS} />;
}
function _temp3(row_0) {
  return <TrBodyRow key={row_0.id} row={row_0} data-testid={`vaults-table-row-${row_0.original.vault.address}`} href={getVaultUrl({
    identifier: InterfaceChainUtils.getIdentifierById(row_0.original.vault.chainId),
    vaultAddress: row_0.original.vault.address,
    vaultName: row_0.original.vault.name
  })} />;
}